import React from "react";
import {
  Drawer,
  Modal,
  List,
  Card,
  Progress,
  Button,
  Space,
  Typography,
  Tag,
  Grid,
  Divider,
  Popconfirm,
} from "antd";
import {
  CheckCircleOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  BookOutlined,
  RocketOutlined,
  TableOutlined,
  ImportOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useGuide } from "../context/GuideContext";
import type { GuideId } from "../types";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

// Guide metadata for display
interface GuideInfo {
  id: GuideId;
  titleKey: string;
  descriptionKey: string;
  icon: React.ReactNode;
  category: "essential" | "dataManagement" | "workflow" | "customization";
}

const GUIDES: GuideInfo[] = [
  {
    id: "dashboard-overview",
    titleKey: "tours.dashboardOverview.title",
    descriptionKey: "tours.dashboardOverview.description",
    icon: <RocketOutlined />,
    category: "essential",
  },
  {
    id: "entity-management",
    titleKey: "tours.entityManagement.title",
    descriptionKey: "tours.entityManagement.description",
    icon: <BookOutlined />,
    category: "dataManagement",
  },
  {
    id: "import-wizard",
    titleKey: "tours.importWizard.title",
    descriptionKey: "tours.importWizard.description",
    icon: <ImportOutlined />,
    category: "dataManagement",
  },
  {
    id: "workflow-inbasket",
    titleKey: "tours.workflowInbasket.title",
    descriptionKey: "tours.workflowInbasket.description",
    icon: <SyncOutlined />,
    category: "workflow",
  },
  {
    id: "lifecycle-states",
    titleKey: "tours.lifecycleStates.title",
    descriptionKey: "tours.lifecycleStates.description",
    icon: <SyncOutlined />,
    category: "workflow",
  },
  {
    id: "column-management",
    titleKey: "tours.columnManagement.title",
    descriptionKey: "tours.columnManagement.description",
    icon: <TableOutlined />,
    category: "customization",
  },
];

/**
 * HelpCenter - Drawer/Modal showing all available guides
 */
export const HelpCenter: React.FC = () => {
  const { t } = useTranslation("guide");
  const screens = useBreakpoint();
  const {
    helpCenterOpen,
    closeHelpCenter,
    startTour,
    isGuideComplete,
    getGuideProgress,
    resetGuideProgress,
  } = useGuide();

  const isMobile = !screens.md;
  const { completed, total } = getGuideProgress();
  const progressPercent = Math.round((completed / total) * 100);

  const handleStartTour = (guideId: GuideId) => {
    closeHelpCenter();
    // Small delay to allow drawer to close before starting tour
    setTimeout(() => {
      startTour(guideId);
    }, 300);
  };

  const renderGuideItem = (guide: GuideInfo) => {
    const isComplete = isGuideComplete(guide.id);

    return (
      <List.Item key={guide.id}>
        <Card
          hoverable
          size="small"
          style={{ width: "100%" }}
          styles={{
            body: { padding: "12px 16px" },
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Space>
              <span
                style={{
                  fontSize: 20,
                  color: isComplete ? "#52c41a" : "#1890ff",
                }}
              >
                {isComplete ? <CheckCircleOutlined /> : guide.icon}
              </span>
              <div>
                <Text strong>{t(guide.titleKey)}</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {t(guide.descriptionKey)}
                </Text>
              </div>
            </Space>
            <Space>
              {isComplete && (
                <Tag color="success" style={{ margin: 0 }}>
                  {t("actions.finish")}
                </Tag>
              )}
              <Button
                type={isComplete ? "default" : "primary"}
                size="small"
                icon={<PlayCircleOutlined />}
                onClick={() => handleStartTour(guide.id)}
              >
                {isComplete ? t("actions.restartTour") : t("actions.startTour")}
              </Button>
            </Space>
          </div>
        </Card>
      </List.Item>
    );
  };

  const content = (
    <div style={{ padding: isMobile ? 0 : "0 8px" }}>
      {/* Progress Section */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ flex: 1, marginRight: 16 }}>
            <Text strong>{t("helpCenter.progress")}</Text>
            <Progress
              percent={progressPercent}
              size="small"
              status={progressPercent === 100 ? "success" : "active"}
              format={() =>
                t("onboarding.progress.completed", { completed, total })
              }
            />
          </div>
          <Popconfirm
            title={t("helpCenter.resetConfirm")}
            onConfirm={resetGuideProgress}
            okText={t("actions.gotIt")}
            cancelText={t("actions.close")}
          >
            <Button
              size="small"
              icon={<ReloadOutlined />}
              disabled={completed === 0}
            >
              {t("helpCenter.resetProgress")}
            </Button>
          </Popconfirm>
        </div>
      </Card>

      {/* Guides List */}
      <Title level={5} style={{ marginBottom: 12 }}>
        {t("helpCenter.featureGuides")}
      </Title>
      <List
        dataSource={GUIDES}
        renderItem={renderGuideItem}
        split={false}
        style={{ marginBottom: 16 }}
      />

      <Divider />

      {/* Support Link */}
      <div style={{ textAlign: "center", padding: "8px 0" }}>
        <Text type="secondary">{t("helpCenter.contactSupport")}</Text>
      </div>
    </div>
  );

  // Use Drawer on mobile, Modal on desktop
  if (isMobile) {
    return (
      <Drawer
        title={t("helpCenter.title")}
        placement="bottom"
        height="85vh"
        open={helpCenterOpen}
        onClose={closeHelpCenter}
        styles={{
          body: { padding: "16px" },
        }}
      >
        {content}
      </Drawer>
    );
  }

  return (
    <Modal
      title={t("helpCenter.title")}
      open={helpCenterOpen}
      onCancel={closeHelpCenter}
      footer={null}
      width={600}
      styles={{
        body: { maxHeight: "70vh", overflowY: "auto", padding: "16px 24px" },
      }}
    >
      {content}
    </Modal>
  );
};
